import { createResourceCollection } from '@/request'
import { ResourceModel } from '@/request/types'
import { useAppDispatch } from '@/request/utils'

export type ThermostatInactivityPeriod = {
  id: string
  thermostat: number
  start_time: string // Time format: "HH:MM"
  duration_seconds: number
  days_of_week: number[] // 0-6 indices for Mon-Sun (backend uses Monday as 0)
} & ResourceModel

export type InactiveTimeUI = {
  id: string
  startTime: string // format: HH:mm (24h)
  endTime: string // format: HH:mm (24h)
  days: number[] // 0-6 indices for Sun-Sat (frontend uses Sunday as 0)
}

// Transform from frontend UI format to backend API format
export function transformToApiFormat(
  uiData: InactiveTimeUI,
  thermostatId: number,
): Omit<ThermostatInactivityPeriod, keyof ResourceModel> {
  const startTime = uiData.startTime
  const [startHours, startMinutes] = startTime.split(':').map(Number)
  const [endHours, endMinutes] = uiData.endTime.split(':').map(Number)

  const startMinutesTotal = startHours * 60 + startMinutes
  const endMinutesTotal = endHours * 60 + endMinutes

  // Calculate duration, handling overnight periods
  let durationMinutes = endMinutesTotal - startMinutesTotal
  if (durationMinutes <= 0) {
    // Overnight period
    durationMinutes = 24 * 60 + durationMinutes
  }

  // Convert frontend day indices (Sun=0) to backend day indices (Mon=0)
  // Frontend: [Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6]
  // Backend:  [Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6]
  const backendDays = uiData.days.map((frontendDay) => {
    return frontendDay === 0 ? 6 : frontendDay - 1
  })

  return {
    id: uiData.id,
    thermostat: thermostatId,
    start_time: startTime,
    duration_seconds: Math.min(durationMinutes * 60, 86400),
    days_of_week: backendDays,
  }
}

// Transform from backend API format to frontend UI format
export function transformFromApiFormat(
  apiData: ThermostatInactivityPeriod,
): InactiveTimeUI {
  const startTime = apiData.start_time
  const durationMinutes = Math.floor(apiData.duration_seconds / 60)

  const [startHours, startMinutes] = startTime.split(':').map(Number)
  const startMinutesTotal = startHours * 60 + startMinutes
  const endMinutesTotal = startMinutesTotal + durationMinutes

  // Handle overnight periods
  const endHours = Math.floor((endMinutesTotal % (24 * 60)) / 60)
  const endMinutesRemainder = endMinutesTotal % 60

  const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutesRemainder
    .toString()
    .padStart(2, '0')}`

  // Convert backend day indices (Mon=0) to frontend day indices (Sun=0)
  // Backend:  [Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6]
  // Frontend: [Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6]
  const frontendDays = apiData.days_of_week.map((backendDay) => {
    return backendDay === 6 ? 0 : backendDay + 1
  })

  return {
    id: apiData.id,
    startTime,
    endTime,
    days: frontendDays,
  }
}

// Helper function to construct the path with type safety
export function createThermostatInactivityPeriodsPath(params: {
  thermostatId: number
}): string {
  return `/thermostats/${params.thermostatId}/inactivity_periods/`
}

// Custom hook that uses the path helper
export function useThermostatInactivityPeriodsCollection(thermostatId: number) {
  const path = createThermostatInactivityPeriodsPath({ thermostatId })
  return thermostatInactivityPeriodsCollection.useFetch(path)
}

// Custom hook for mutations on thermostat inactivity periods
export function useThermostatInactivityPeriodMutations(thermostatId: number) {
  const path = createThermostatInactivityPeriodsPath({ thermostatId })
  const { createAction, status, error, data } =
    thermostatInactivityPeriodsCollection.useCreate({ path })
  const dispatch = useAppDispatch()

  const create = async (uiData: InactiveTimeUI): Promise<InactiveTimeUI> => {
    const apiData = transformToApiFormat(uiData, thermostatId)
    const actionResponse: any = await dispatch(createAction(apiData))
    const created = actionResponse?.payload as
      | ThermostatInactivityPeriod
      | undefined
    if (!created) {
      throw new Error('Failed to create thermostat inactivity period')
    }
    return transformFromApiFormat(created)
  }

  const update = async (uiData: InactiveTimeUI): Promise<void> => {
    const apiData = transformToApiFormat(uiData, thermostatId)
    return dispatch(
      thermostatInactivityPeriodsCollection.actions.update(uiData.id, apiData, {
        pathSegments: [
          'thermostats',
          thermostatId.toString(),
          'inactivity_periods',
        ],
      }),
    )
  }

  const remove = async (id: string | number): Promise<void> => {
    return dispatch(
      thermostatInactivityPeriodsCollection.actions.remove(id, {
        pathSegments: [
          'thermostats',
          thermostatId.toString(),
          'inactivity_periods',
        ],
      }),
    )
  }

  return {
    create,
    update,
    remove,
    status,
    error,
    data,
  }
}

// Legacy hook for backward compatibility
export function useCreateThermostatInactivityPeriod(thermostatId: number) {
  const mutations = useThermostatInactivityPeriodMutations(thermostatId)
  return {
    create: mutations.create,
    status: mutations.status,
    error: mutations.error,
    data: mutations.data,
  }
}

export const thermostatInactivityPeriodsCollection =
  createResourceCollection<ThermostatInactivityPeriod>({
    name: 'thermostatInactivityPeriods',
    selector: (state) => state.thermostatInactivityPeriods,
  })

export default thermostatInactivityPeriodsCollection.slice.reducer
